import { useT } from "@transifex/react";
import { getTeacherCommentStyles, type TeacherCommentTones } from "../../../constants/StudentResultCommentTones";

/** Whether a comment holds anything worth a bubble — the one place blank and whitespace-only are ruled out. */
export const getHasTeacherComment = (comment: string | undefined): comment is string => Boolean(comment?.trim());

interface StudentResultTeacherCommentProps {
    comment: string;
    tone: TeacherCommentTones;
    /** The single comment on the whole test rather than on one answer. */
    isOverall?: boolean;
}

/**
 * The teacher's free-text comment, read-only. The square top-right corner is the design's hint of a
 * speech bubble pointing back at what it comments on — the answer above it, or the whole test. The
 * text wraps and keeps the teacher's line breaks; Figma's single line is only the placeholder.
 * A blank comment renders nothing, so callers need no guard of their own.
 */
const StudentResultTeacherComment = (props: StudentResultTeacherCommentProps) => {
    const t = useT();
    const t_sr_answerComment = t("Comment from your teacher:", {
        _tags: "StudentResult, TeacherComment, ScreenReaderOnly",
    });
    const t_sr_overallComment = t("Comment from your teacher on the whole test:", {
        _tags: "StudentResult, TeacherComment, ScreenReaderOnly",
    });

    if (!getHasTeacherComment(props.comment)) return null;

    const styles = getTeacherCommentStyles(props.tone);

    return (
        <div
            className={`bg-white border rounded-[12px] rounded-tr-none px-m py-s ${styles.border}`}
            data-testid={props.isOverall ? "student-result-overall-comment" : "student-result-teacher-comment"}
        >
            {/* The per-answer bubble centres its text; the whole-test comment reads as a paragraph. */}
            <p
                className={`text-m whitespace-pre-wrap break-words ${props.isOverall ? "" : "text-center"} ${
                    styles.text
                }`}
                data-testid="student-result-comment-text"
            >
                <span className="sr-only">{props.isOverall ? t_sr_overallComment : t_sr_answerComment} </span>
                {props.comment}
            </p>
        </div>
    );
};

export default StudentResultTeacherComment;
